import React from 'react';
import ReactDOM from 'react-dom';
import * as ReactRouterDOM from 'react-router-dom';
import * as ReactJSXRuntime from 'react/jsx-runtime';
import * as i18next from 'i18next';
import * as ReactI18next from 'react-i18next';
import { getClient } from './api';

// Cache loaded mini-app modules by appId
const loadedApps = new Map<string, { Router: React.ComponentType; css: string | null; version: string }>();

/**
 * Clear cached bundle for an app so it gets re-fetched on next load.
 */
export function clearCache(appId: string) {
  loadedApps.delete(appId);
}

export interface LoadedMiniApp {
  Router: React.ComponentType;
  /** CSS text adopted into the shadow root, or null if no styles. */
  css: string | null;
}

/**
 * Load a mini-app's web bundle (JS + optional CSS) and return them together.
 *
 * CSS is fetched in parallel with the JS bundle and returned as a plain string.
 * The caller is responsible for adopting the CSS into the shadow root via
 * `adoptedStyleSheets` before the React tree renders.
 */
export async function loadMiniApp(appId: string): Promise<LoadedMiniApp | null> {
  const cached = loadedApps.get(appId);

  // If cached, check for updates in background
  if (cached) {
    checkForOTAUpdate(appId, cached.version);
    return { Router: cached.Router, css: cached.css };
  }

  const [jsUrl, cssUrl] = await Promise.all([
    getClient().getBundleUrl(appId),
    getClient().getCssBundleUrl(appId),
  ]);

  // Fetch JS and CSS in parallel; CSS may not exist (404) — handle gracefully.
  const [jsRes, cssRes] = await Promise.all([
    fetch(jsUrl),
    fetch(cssUrl).catch(() => null),
  ]);

  if (!jsRes.ok) return null;

  const code = await jsRes.text();
  const css  = (cssRes?.ok ? await cssRes.text() : null);

  const shimmedCode = rewriteImports(code);
  const blob = new Blob([shimmedCode], { type: 'application/javascript' });
  const blobUrl = URL.createObjectURL(blob);

  try {
    const mod = await import(/* @vite-ignore */ blobUrl);
    const Router = mod.Router || mod.default?.Router || mod.default;

    if (Router) {
      const version = await getAppVersion(appId);
      loadedApps.set(appId, { Router, css, version });
      return { Router, css };
    }
    return null;
  } catch (err) {
    URL.revokeObjectURL(blobUrl);
    throw err;
  }
}

/**
 * Get the current active version of an app from the server.
 */
async function getAppVersion(appId: string): Promise<string> {
  try {
    const result = await getClient().checkForUpdate(appId);
    return result.latest_version;
  } catch {
    return 'unknown';
  }
}

/**
 * Check for OTA update in background. If a new version is available,
 * dispatch a custom event so the MiniAppPage can reload.
 */
async function checkForOTAUpdate(appId: string, currentVersion: string): Promise<void> {
  try {
    const result = await getClient().checkForUpdate(appId, currentVersion);
    if (result.update_available) {
      clearCache(appId);
      window.dispatchEvent(new CustomEvent('mini-app-ota-update', {
        detail: { appId, from: currentVersion, to: result.latest_version },
      }));
    }
  } catch {
    // Silent fail — OTA check is best-effort
  }
}

/**
 * Rewrite ESM import statements to reference window.__modules.
 * Converts `import { X as Y } from 'react'` → `const { X: Y } = __ext_react;`
 */
function rewriteImports(code: string): string {
  const externals = ['react', 'react-dom', 'react-dom/client', 'react-router-dom', 'react/jsx-runtime', '@next-qr/mini-app-sdk', 'i18next', 'react-i18next', 'zustand', 'zustand/vanilla'];

  const shims: string[] = [];
  let shimmedCode = code;

  for (const ext of externals) {
    const safeVar = '__ext_' + ext.replace(/[^a-zA-Z0-9]/g, '_');
    shims.push(`const ${safeVar} = window.__modules[${JSON.stringify(ext)}];`);

    const escaped = ext.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');

    // import { a, b as c } from 'ext' → const { a, b: c } = __ext_...;
    shimmedCode = shimmedCode.replace(
      new RegExp(`import\\s*\\{([^}]+)\\}\\s*from\\s*["']${escaped}["'];?`, 'g'),
      (_match, bindings: string) => {
        // Replace " as identifierName" with ": identifierName".
        // Using \s+as\s+ (not \bas\b) avoids incorrectly replacing the
        // local alias when esbuild uses "as" as a minified variable name,
        // e.g. "jsxs as as" must become "jsxs: as", not "jsxs : :".
        const fixed = bindings.replace(/\s+as\s+/g, ': ');
        return `const {${fixed}} = ${safeVar};`;
      },
    );

    // import * as X from 'ext' → const X = __ext_...;
    shimmedCode = shimmedCode.replace(
      new RegExp(`import\\s*\\*\\s*as\\s+([\\w$]+)\\s+from\\s*["']${escaped}["'];?`, 'g'),
      `const $1 = ${safeVar};`,
    );

    // import X, { a, b as c } from 'ext' → const X = ...; const { a, b: c } = ...;
    shimmedCode = shimmedCode.replace(
      new RegExp(`import\\s+([\\w$]+)\\s*,\\s*\\{([^}]+)\\}\\s*from\\s*["']${escaped}["'];?`, 'g'),
      (_match, defaultName: string, bindings: string) => {
        const fixed = bindings.replace(/\bas\b/g, ':');
        return `const ${defaultName} = ${safeVar}.default || ${safeVar};\nconst {${fixed}} = ${safeVar};`;
      },
    );

    // import X from 'ext' → const X = __ext_...(default);
    shimmedCode = shimmedCode.replace(
      new RegExp(`import\\s+([\\w$]+)\\s+from\\s*["']${escaped}["'];?`, 'g'),
      `const $1 = ${safeVar}.default || ${safeVar};`,
    );
  }

  return shims.join('\n') + '\n' + shimmedCode;
}

// Register shared modules on window for mini-app bundles
const w = window as any;
w.__modules = w.__modules || {};
w.__modules['react'] = React;
w.__modules['react-dom'] = ReactDOM;
w.__modules['react-dom/client'] = ReactDOM;
w.__modules['react-router-dom'] = ReactRouterDOM;
w.__modules['react/jsx-runtime'] = ReactJSXRuntime;

// Lazy-load @next-qr/mini-app-sdk when first accessed
import * as MiniAppSDK from '@next-qr/mini-app-sdk';
w.__modules['@next-qr/mini-app-sdk'] = MiniAppSDK;
w.__modules['i18next'] = i18next;
w.__modules['react-i18next'] = ReactI18next;

import * as zustand from 'zustand';
import * as zustandVanilla from 'zustand/vanilla';
w.__modules['zustand'] = zustand;
w.__modules['zustand/vanilla'] = zustandVanilla;
